<template>
  <DialogPrime v-model:visible="localVisible" modal maximizable :draggable="false" class="change-log-dialog"
    :style="{ width: 'min(1200px, 96vw)' }" :breakpoints="{ '768px': '100vw' }" @show="onShow">
    <template #header>
      <div class="d-flex align-items-center gap-3">
        <span class="change-log-icon"><i class="pi pi-history"></i></span>
        <div>
          <h6 class="mb-0 fw-bold text-dark">Change Log Item</h6>
          <small class="text-muted">Who changed what, and when, for one MCR item</small>
        </div>
      </div>
    </template>

    <!-- Pilih item + filter -->
    <div class="d-flex flex-wrap align-items-end gap-3 mb-3">
      <div class="change-log-item-picker">
        <label class="form-label mb-1 small fw-semibold">MCR Item</label>
        <SelectPrime v-model="itemCode" :options="itemOptions" option-label="label" option-value="value" filter
          size="small" placeholder="Select MCR item" fluid @change="load(true)" />
      </div>
      <div>
        <label class="form-label mb-1 small fw-semibold d-block">Changed in</label>
        <SelectButton v-model="sourceFilter" :options="SOURCE_FILTERS" option-label="label" option-value="value"
          :allow-empty="false" size="small" />
      </div>
      <div class="d-flex align-items-center gap-2 ms-auto">
        <span class="change-log-legend is-user"><i class="pi pi-user"></i> User Form</span>
        <span class="change-log-legend is-cataloguer"><i class="pi pi-book"></i> Cataloguer Form</span>
        <ButtonPrime v-tooltip.top="'Reload'" icon="pi pi-refresh" text rounded size="small" :loading="loading"
          :disabled="!itemCode" @click="load(true)" />
      </div>
    </div>

    <div v-if="loading && !loaded">
      <Skeleton v-for="n in 5" :key="n" height="2.6rem" class="mb-2" />
    </div>
    <div v-else-if="error" class="change-log-empty">
      <i class="pi pi-exclamation-triangle"></i>
      <div>Failed to load the change log.</div>
      <ButtonPrime label="Try again" size="small" outlined @click="load(true)" />
    </div>
    <div v-else-if="!itemCode" class="change-log-empty">
      <i class="pi pi-inbox"></i>
      <div>Select an MCR item to see its change log.</div>
    </div>

    <DataTable v-else v-model:expanded-rows="expandedRows" :value="filteredLogs" data-key="_key" size="small"
      striped-rows scrollable scroll-height="55vh" :paginator="filteredLogs.length > 20" :rows="20"
      :rows-per-page-options="[20, 50, 100]" class="change-log-table">
      <Column expander header-class="bg-light" style="width: 3rem" />
      <Column header="Updated By" header-class="bg-light" :style="{ minWidth: '220px' }">
        <template #body="{ data }">
          <div class="d-flex align-items-center gap-2">
            <span class="change-log-avatar">{{ initials(data.user?.name || data.user_id) }}</span>
            <div class="lh-sm">
              <div class="fw-semibold">{{ data.user?.name || data.user_id || '' }}</div>
              <small class="text-muted">{{ data.user?.namecode || '' }}</small>
            </div>
          </div>
        </template>
      </Column>
      <Column header="Action" header-class="bg-light" :style="{ minWidth: '120px' }">
        <template #body="{ data }">
          <span class="change-log-action" :class="`is-${actionKind(data.event)}`">{{ actionLabel(data.event) }}</span>
        </template>
      </Column>
      <Column header="Changed in" header-class="bg-light" :style="{ minWidth: '200px' }">
        <template #body="{ data }">
          <div class="d-flex flex-wrap align-items-center gap-1">
            <span v-if="data._sources.user" class="change-log-legend is-user"><i class="pi pi-user"></i> User</span>
            <span v-if="data._sources.cataloguer" class="change-log-legend is-cataloguer"><i class="pi pi-book"></i> Cataloguer</span>
            <small class="text-muted">{{ data._changedCount }} {{ data._changedCount === 1 ? 'field' : 'fields' }}</small>
          </div>
          <small v-if="data.model_name" class="text-muted d-block">{{ humanizeModel(data.model_name) }}</small>
        </template>
      </Column>
      <Column header="Created At" header-class="bg-light" :style="{ minWidth: '150px' }">
        <template #body="{ data }">{{ formatDateTime(data.created_at) }}</template>
      </Column>

      <template #expansion="{ data }">
        <div class="change-log-expansion">
          <div class="d-flex justify-content-end mb-2">
            <div class="d-flex align-items-center gap-2 small">
              <ToggleSwitch v-model="changedOnly" input-id="change-log-changed-only" />
              <label for="change-log-changed-only" class="mb-0">Show changed fields only</label>
            </div>
          </div>
          <div class="row g-3">
            <div v-for="side in SIDES" :key="side.key" class="col-md-6">
              <div class="card change-log-card h-100" :class="`is-${side.key}`">
                <div class="card-header py-2 d-flex align-items-center gap-2">
                  <i :class="side.icon"></i>
                  <span class="fw-semibold">{{ side.label }}</span>
                </div>
                <div class="card-body p-0">
                  <div v-if="!visibleGroups(data).length" class="text-muted small text-center py-3">No field values.</div>
                  <template v-for="group in visibleGroups(data)" :key="group.key">
                    <div class="change-log-group" :class="`is-${group.key}`">
                      <i :class="group.icon"></i> {{ group.label }}
                    </div>
                    <div v-for="field in group.fields" :key="field" class="change-log-row"
                      :class="{ 'is-changed': data._changed.has(field) }">
                      <span class="change-log-field" :title="field">{{ humanizeField(field) }}</span>
                      <span class="change-log-value" :title="displayValue(data[side.key]?.[field])"
                        :class="{ 'is-empty': isEmpty(data[side.key]?.[field]) }">
                        {{ displayValue(data[side.key]?.[field]) }}
                      </span>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #empty>
        <div class="change-log-empty">
          <i class="pi pi-inbox"></i>
          <div>{{ logs.length ? 'No changes for this filter.' : 'No change log for this item yet.' }}</div>
        </div>
      </template>
    </DataTable>

    <template #footer>
      <ButtonPrime label="Close" size="small" @click="localVisible = false" />
    </template>
  </DialogPrime>
</template>

<script>
import api from "@/helpers/api";
import dataLogService from "@/services/report/appAuditing/dataLogService";

const SOURCE_FILTERS = [
  { label: "All", value: "all" },
  { label: "User Form", value: "user" },
  { label: "Cataloguer Form", value: "cataloguer" }
];

const SIDES = [
  { key: "old_values", label: "Old Values", icon: "pi pi-minus-circle" },
  { key: "new_values", label: "New Values", icon: "pi pi-plus-circle" }
];

// Kolom teknis (identitas & audit) dikelompokkan terpisah supaya perubahan isian form lebih mudah dibaca.
const SYSTEM_FIELDS = new Set(["id", "client", "mcr_code", "mcr_item_code", "created_by", "updated_by", "created_at", "updated_at", "status"]);

const GROUPS = [
  { key: "user", label: "User Form", icon: "pi pi-user" },
  { key: "cataloguer", label: "Cataloguer Form", icon: "pi pi-book" },
  { key: "system", label: "System", icon: "pi pi-cog" }
];

const ACTIONS = {
  created: { label: "Created", kind: "created" },
  updated: { label: "Updated", kind: "updated" },
  deleted: { label: "Deleted", kind: "deleted" },
  restored: { label: "Restored", kind: "created" }
};

const asObject = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});

export default {
  name: "ItemChangeLogModal",
  props: {
    visible: { type: Boolean, default: false },
    // Item MCR yang bisa dipilih (store mcr.allMcrItem) & item yang sedang dipilih di form (pilihan awal).
    items: { type: Array, default: () => [] },
    selectedItemCode: { type: String, default: "" }
  },
  emits: ["update:visible"],
  data() {
    return {
      SOURCE_FILTERS,
      SIDES,
      itemCode: "",
      sourceFilter: "all",
      changedOnly: true,
      logs: [],
      loadedCode: "",
      loading: false,
      loaded: false,
      error: false,
      expandedRows: {}
    };
  },
  computed: {
    localVisible: {
      get() { return this.visible; },
      set(value) { this.$emit("update:visible", value); }
    },
    itemOptions() {
      return this.items.filter((item) => item?.mcr_item_code).map((item) => {
        const detail = [item.material_number, item.cat_material_type || item.material_type].filter(Boolean).join(" · ");
        return { value: item.mcr_item_code, label: detail ? `${item.mcr_item_code} — ${detail}` : item.mcr_item_code };
      });
    },
    filteredLogs() {
      if (this.sourceFilter === "all") return this.logs;
      return this.logs.filter((log) => log._sources[this.sourceFilter]);
    }
  },
  methods: {
    // Tiap dibuka: item yang sedang dipilih di form jadi pilihan awal (kalau belum ada pilihan / item berganti).
    onShow() {
      const preferred = this.selectedItemCode || this.itemOptions[0]?.value || "";
      if (preferred && preferred !== this.itemCode) this.itemCode = preferred;
      this.load();
    },
    async load(force = false) {
      if (!this.itemCode || this.loading) return;
      if (!force && this.loaded && this.loadedCode === this.itemCode) return;
      this.loading = true;
      this.error = false;
      this.expandedRows = {};
      try {
        const response = await api(this, dataLogService.getByEmcrItem(this.itemCode), false);
        const list = Array.isArray(response?.data) ? response.data : (response?.data?.data || []);
        this.logs = list.map((log, index) => this.decorate(log, index));
        this.loadedCode = this.itemCode;
        this.loaded = true;
      } catch (e) {
        this.error = true;
        this.logs = [];
      } finally {
        this.loading = false;
      }
    },
    // Field `cat_*` (atau tabel Cat*) = perubahan dari form Cataloguer, selain itu form User; kolom teknis = System.
    fieldGroup(field, modelName) {
      if (SYSTEM_FIELDS.has(field)) return "system";
      if (field.startsWith("cat_") || String(modelName || "").startsWith("Cat")) return "cataloguer";
      return "user";
    },
    decorate(log, index) {
      const oldValues = asObject(log.old_values);
      const newValues = asObject(log.new_values);
      const keys = [...new Set([...Object.keys(oldValues), ...Object.keys(newValues)])];
      const changed = new Set(keys.filter((key) => {
        if (log.event === "created") return !this.isEmpty(newValues[key]);
        if (log.event === "deleted") return !this.isEmpty(oldValues[key]);
        return JSON.stringify(oldValues[key] ?? null) !== JSON.stringify(newValues[key] ?? null);
      }));
      const sources = { user: false, cataloguer: false };
      changed.forEach((key) => {
        const group = this.fieldGroup(key, log.model_name);
        if (group !== "system") sources[group] = true;
      });
      return {
        ...log,
        old_values: oldValues,
        new_values: newValues,
        _key: `${log.id ?? "log"}-${index}`,
        _keys: keys,
        _changed: changed,
        _changedCount: [...changed].filter((key) => !SYSTEM_FIELDS.has(key)).length,
        _sources: sources
      };
    },
    // Field yang ditampilkan di kedua card (urutan sama, supaya baris kiri & kanan sejajar), dikelompokkan User / Cataloguer / System.
    visibleGroups(log) {
      const fields = this.changedOnly ? log._keys.filter((key) => log._changed.has(key)) : log._keys;
      return GROUPS.map((group) => ({
        ...group,
        fields: fields.filter((key) => this.fieldGroup(key, log.model_name) === group.key)
      })).filter((group) => group.fields.length);
    },
    isEmpty(value) {
      return value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length);
    },
    displayValue(value) {
      if (this.isEmpty(value)) return "—";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    humanizeField(field) {
      return field.replace(/^cat_/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    humanizeModel(model) {
      return String(model).replace(/([a-z])([A-Z])/g, "$1 $2");
    },
    actionLabel(event) {
      return ACTIONS[event]?.label || String(event || "-").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    actionKind(event) {
      return ACTIONS[event]?.kind || "updated";
    },
    initials(name) {
      return String(name || "S").split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join("");
    },
    // DD-MM-YYYY HH:mm (waktu lokal).
    formatDateTime(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
  }
};
</script>

<style scoped>
.change-log-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e8f1fb;
  color: #185ea2;
}
.change-log-item-picker {
  min-width: 320px;
  flex: 1 1 320px;
  max-width: 460px;
}
.change-log-legend {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
}
.change-log-legend.is-user {
  background: #eef2f6;
  color: #344054;
}
.change-log-legend.is-cataloguer {
  background: #f3ecfb;
  color: #6b3fa0;
}
.change-log-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #185ea2;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
}
.change-log-action {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.change-log-action.is-created {
  background: #e9f7ef;
  color: #1e7b45;
}
.change-log-action.is-updated {
  background: #e8f1fb;
  color: #185ea2;
}
.change-log-action.is-deleted {
  background: #fdecec;
  color: #b42318;
}
.change-log-expansion {
  padding: 12px 8px;
  background: #fafbfc;
}
.change-log-card {
  box-shadow: none !important;
  border: 1px solid #e5e7eb;
}
.change-log-card.is-old_values .card-header {
  background: #fdf3f2;
  color: #b42318;
}
.change-log-card.is-new_values .card-header {
  background: #eef8f2;
  color: #1e7b45;
}
.change-log-group {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 6px 12px;
  border-top: 1px solid #eef0f3;
  background: #f8fafc;
  color: #475467;
}
.change-log-group.is-cataloguer {
  color: #6b3fa0;
}
.change-log-group.is-system {
  color: #98a2b3;
}
.change-log-row {
  display: grid;
  grid-template-columns: minmax(120px, 42%) 1fr;
  gap: 8px;
  padding: 6px 12px;
  border-top: 1px solid #f2f4f7;
  font-size: 0.8rem;
  min-height: 32px;
  align-items: center;
}
.change-log-field {
  color: #667085;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.change-log-value {
  color: #101828;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.change-log-value.is-empty {
  color: #98a2b3;
  font-weight: 400;
}
.change-log-card.is-old_values .change-log-row.is-changed {
  background: #fff6f5;
}
.change-log-card.is-new_values .change-log-row.is-changed {
  background: #f3fbf6;
}
.change-log-empty {
  text-align: center;
  padding: 40px 0;
  color: #667085;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.change-log-empty i {
  font-size: 1.8rem;
  color: #98a2b3;
}
</style>
